/* eslint-disable no-void */
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ForgeCustomFieldValue } from '@atlassian/jira-forge-ui-types/src/common/types/contexts/custom-field.tsx';
import { useInlineField } from '../index.tsx';
import {
	FOCUSED_FIELD_STATE,
	IDLE_FIELD_STATE,
	PENDING_FIELD_STATE,
	STORED_FIELD_STATE,
	FAILED_FIELD_STATE,
} from '../constants.tsx';

interface Props {
	fieldId: string;
	containerRef: React.RefObject<HTMLDivElement | null>;
	onSubmit: (fieldValue: ForgeCustomFieldValue) => void;
}

export const useForgeCustomFieldStateSync = ({ fieldId, containerRef, onSubmit }: Props) => {
	const [fieldState, { setFieldState }] = useInlineField(fieldId);
	const fieldStateRef = useRef(fieldState);
	const recentSubmitIdRef = useRef(Symbol('submit-id'));

	const isInFocus = useCallback(() => {
		return (
			typeof globalThis !== 'undefined' &&
			globalThis.document &&
			(containerRef.current === globalThis.document.activeElement ||
				containerRef.current?.contains(globalThis.document.activeElement))
		);
	}, [containerRef]);

	const syncFieldFocusState = useCallback(() => {
		if (isInFocus()) {
			void setFieldState({
				fieldId,
				status: FOCUSED_FIELD_STATE,
			});
		} else if (fieldStateRef.current?.status === FOCUSED_FIELD_STATE) {
			void setFieldState({
				fieldId,
				status: PENDING_FIELD_STATE,
			});
		}
	}, [isInFocus, setFieldState, fieldId]);

	/**
	 * This function is called when the form is submitted.
	 * It is used to sync the actual state of the field with our internal storage.
	 * This is necessary because the Forge fields defined using Custom UI are rendered in the iframe and not all their focus/blur events are propagated to the parent.
	 * We also want to manually trigger the blur event on the field so attached blur handlers kick in and the field's value gets submitted.
	 */
	const onFormSubmit = useCallback(() => {
		syncFieldFocusState();
		if (isInFocus()) {
			const doc = typeof globalThis !== 'undefined' ? globalThis.document : undefined;
			const activeElement = doc?.activeElement;

			if (activeElement && 'blur' in activeElement && typeof activeElement.blur === 'function') {
				activeElement.blur();
			}
		}
	}, [syncFieldFocusState, isInFocus]);

	useEffect(
		() => {
			void setFieldState({
				onFormSubmit,
				fieldId,
				status: IDLE_FIELD_STATE,
			});
		},
		// Keep these dependencies immutable, otherwise we are going to override the field's state.
		[fieldId, onFormSubmit, setFieldState],
	);

	useEffect(() => {
		fieldStateRef.current = fieldState;
	}, [fieldState, setFieldState]);

	useEffect(() => {
		syncFieldFocusState();

		// Wait for the focus to be moved to the element.
		// In case of iframes it is not immediate so we must use a slight delay.
		const delayedFieldFocusStateSync = () => setTimeout(syncFieldFocusState, 100);
		if (typeof globalThis !== 'undefined' && globalThis.document) {
			globalThis.document.addEventListener('focusout', delayedFieldFocusStateSync);
		}

		return () => {
			if (typeof globalThis !== 'undefined' && globalThis.document) {
				globalThis.document.removeEventListener('focusout', delayedFieldFocusStateSync);
			}
		};
	}, [syncFieldFocusState, fieldId]);

	/**
	 * Submit bridge method implementation.
	 * It is possible for the app to programmatically submit the field multiple times.
	 * And it may be the case that they submit a promise that takes time to resolve.
	 * Because of that we need to update the state when the last pending submit is resolved.
	 */
	const submit = useCallback(
		async ({ data }: { data: ForgeCustomFieldValue }) => {
			try {
				recentSubmitIdRef.current = Symbol('submit-id');
				const scopedSubmitId = recentSubmitIdRef.current;

				await setFieldState({
					fieldId,
					status: PENDING_FIELD_STATE,
				});

				const resolvedValue = await Promise.resolve(data);
				if (scopedSubmitId === recentSubmitIdRef.current) {
					onSubmit(resolvedValue);
					await setFieldState({
						fieldId,
						status: STORED_FIELD_STATE,
					});
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				setFieldState({
					fieldId,
					status: FAILED_FIELD_STATE,
				});
			}

			return undefined;
		},
		[fieldId, setFieldState, onSubmit],
	);

	return useMemo(() => ({ submit }), [submit]);
};
